import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISeason } from '../../api/client';
import SeasonStatusBadge from './SeasonStatusBadge';
import { seasonLabel } from '../../utils/seasonLabel';
import { formatPrice } from '../../utils/coursePricing';
import '../../App.css';

interface SeasonInfoPanelProps {
  /** The page-selected season (undefined while none is resolved). */
  season?: ISeason;
  /** Whether the selected season is editable (current/upcoming only); drives
   *  the view-only note. Editing itself is triggered from the seasons widget. */
  canEdit: boolean;
}

/** A single label / value row in the read-only season card. */
const InfoRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '0.4rem 0',
      borderBottom: '1px solid var(--bg-light)',
    }}
  >
    <span style={{ color: 'var(--text-muted)' }}>{label}</span>
    <span style={{ textAlign: 'right' }}>{children}</span>
  </div>
);

/**
 * Read-only card summarizing the page-selected season (term / capacity /
 * per-session price / enrollment policy / instructor / status). Editing is
 * delegated to the SeasonForm modal via `onEdit`, and the Edit button is only
 * rendered when the season is editable (current/upcoming). Reuses the shared
 * form-section wrapper, Button, and SeasonStatusBadge primitives — no writes
 * happen here.
 */
const SeasonInfoPanel: React.FC<SeasonInfoPanelProps> = ({ season, canEdit }) => {
  const { t, i18n } = useTranslation();

  const term = useMemo(
    () => seasonLabel(season?.termStart, season?.termEnd, t, i18n.language),
    [season?.termStart, season?.termEnd, t, i18n.language],
  );

  if (!season) {
    return (
      <div className="season-info-card">
        <h4 className="form-section__title" style={{ marginTop: 0 }}>
          {t('courses.seasonInfo.title')}
        </h4>
        <p style={{ color: 'var(--text-muted)' }}>{t('courses.seasons.empty')}</p>
      </div>
    );
  }

  const enrollmentPolicyLabel =
    season.enrollmentPolicy === 'open'
      ? t('courses.enrollmentPolicyOpen')
      : t('courses.enrollmentPolicyApproval');

  const price = formatPrice(season.pricePerSession, i18n.language);

  return (
    <div className="season-info-card">
      <h4 className="form-section__title" style={{ marginTop: 0 }}>
        {t('courses.seasonInfo.title')}
      </h4>

      <div>
        <InfoRow label={t('courses.seasons.term')}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <span>{term}</span>
            <SeasonStatusBadge status={season.status} />
          </span>
        </InfoRow>
        <InfoRow label={t('courses.seasonInfo.capacity')}>
          {season.capacity != null ? season.capacity : t('courses.seasonInfo.capacityUnlimited')}
        </InfoRow>
        <InfoRow label={t('courses.seasonInfo.pricePerSession')}>
          {price ?? t('courses.seasonInfo.notOffered')}
        </InfoRow>
        <InfoRow label={t('courses.seasonInfo.enrollmentPolicy')}>{enrollmentPolicyLabel}</InfoRow>
        <InfoRow label={t('courses.seasonInfo.instructor')}>
          {season.instructorName || t('common.none')}
        </InfoRow>
      </div>

      {!canEdit && (
        <p className="form-help-text" style={{ marginBottom: 0 }}>
          {t('courses.seasonInfo.viewOnly')}
        </p>
      )}
    </div>
  );
};

export default SeasonInfoPanel;
